import {Injectable} from '@angular/core';
import {marked, Renderer, Tokens} from 'marked';
import {markedEmoji} from 'marked-emoji';
import {gemoji} from 'gemoji';

const emojiShortcodes: Record<string, string> = {};
for (const entry of gemoji) {
    for (const name of entry.names) {
        emojiShortcodes[name] = entry.emoji;
    }
}

marked.use(markedEmoji({emojis: emojiShortcodes, renderer: token => token.emoji}));

@Injectable({providedIn: 'root'})
export class BotatoMarkdownService {

    private readonly renderer: Renderer;

    constructor() {
        this.renderer = new marked.Renderer({
            breaks: true,
            gfm: true
        });
        this.renderer.link = (data: Tokens.Link): string => {
            return `<a href="${data.href}" target="_blank" title="${data.title ?? data.text}">${data.text}</a>`;
        };
    }

    public renderMarkdown(value: string, removeEnclosingParagraph: boolean = false): string {
        if (value === undefined || value.length === 0) {
            return value;
        }

        const fixedValue = this.fixNewLines(value);
        const rendered = marked(fixedValue, {renderer: this.renderer, async: false});

        if (removeEnclosingParagraph) {
            return rendered.replace('<p>', '').replace('</p>', '').trim();
        }
        return rendered.trim();
    }


    /**
     * We want to keep newlines as br tags when we convert markdown to html
     * As the standard does collapse them to one line and marked ad a library therefore
     * just creates a single <p> tag for multiple newlines, we parse newlines here manually
     * before using marked afterward and just add <br /> tags where needed
     *
     * We want to keep the newlines for list items, as these should be properly handled by marked
     */
    private fixNewLines(value: string): string {
        let fixedValue = '';
        const lines = value.split(/\r?\n/);

        let isWithingList = false;
        let isWithinTable = false;
        let isWithinCodeBlock = false;
        let isPreviousLineEmpty = false;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const hasNextLine = i < lines.length - 1;
            const nextLine = hasNextLine ? lines[i + 1] : '';

            let separator = '<br />';
            const isFence = this.isCodeFence(line);

            if (isWithinCodeBlock) {
                // Inside a fenced code block everything is verbatim: keep real newlines and
                // never treat a line as markdown.
                separator = '\r\n';
                if (isFence) {
                    // closing fence - terminate the block with a blank line
                    isWithinCodeBlock = false;
                    separator = '\r\n\r\n';
                    if (hasNextLine && nextLine.trim().length === 0) {
                        i++;
                    }
                }
            } else if (isFence) {
                // opening fence - keep real newlines so marked parses the code block
                isWithinCodeBlock = true;
                isWithingList = false;
                isWithinTable = false;
                separator = '\r\n';
            } else {
                const startsTable = this.isTableRow(line) && hasNextLine && this.isTableDelimiter(nextLine);
                const nextStartsTable = !this.isTableRow(line) && hasNextLine && this.isTableRow(nextLine) && i + 2 < lines.length && this.isTableDelimiter(lines[i + 2]);
                const nextIsThematicBreak = hasNextLine && this.isThematicBreak(nextLine);
                const nextStartsBlock = hasNextLine && !this.isBlockquote(line) && (this.isBlockquote(nextLine) || this.isCodeFence(nextLine));
                if (nextStartsTable || nextIsThematicBreak || nextStartsBlock) {
                    // A block element (table, thematic break, blockquote, code fence) directly
                    // after text needs a blank line in between, otherwise marked treats it as
                    // part of the preceding paragraph.
                    separator = '\r\n\r\n';
                    isWithingList = false;
                    isWithinTable = false;
                } else if (startsTable || (isWithinTable && this.isTableRow(line))) {
                    isWithingList = false;
                    if (hasNextLine && this.isTableRow(nextLine)) {
                        // still inside the table, keep the newline so marked sees consecutive rows
                        separator = '\r\n';
                        isWithinTable = true;
                    } else {
                        // last row of the table - marked only terminates a table on a blank line,
                        // so close it explicitly and swallow a following blank line to avoid a stray <br />
                        separator = '\r\n\r\n';
                        isWithinTable = false;
                        if (hasNextLine && nextLine.trim().length === 0) {
                            i++;
                        }
                    }
                } else if (this.isBlockquote(line)) {
                    // Keep consecutive blockquote lines as a single blockquote, and close it
                    // with a blank line when the quote ends (otherwise <br /> tags would merge
                    // the lines and the following content into one quote).
                    isWithingList = false;
                    isWithinTable = false;
                    separator = hasNextLine && this.isBlockquote(nextLine) ? '\r\n' : '\r\n\r\n';
                } else if (this.isListItem(line) || (isWithingList && !isPreviousLineEmpty)) {
                    separator = '\r\n';
                    isWithingList = true;
                    isWithinTable = false;
                } else if (hasNextLine && this.isListItem(nextLine)) {
                    separator = '\r\n';
                    isWithingList = true;
                    isWithinTable = false;
                } else if (this.isHeading(line) || (hasNextLine && this.isHeading(nextLine))) {
                    separator = '\r\n';
                    isWithingList = false;
                    isWithinTable = false;
                } else if (this.isThematicBreak(line)) {
                    // A thematic break must be its own block; isolate it with a blank line so
                    // marked renders an <hr> instead of literal --- text glued by <br /> tags.
                    separator = '\r\n\r\n';
                    isWithingList = false;
                    isWithinTable = false;
                } else {
                    isWithinTable = false;
                    if (isPreviousLineEmpty && isWithingList) {
                        fixedValue += '<br />';
                        isWithingList = false;
                    }
                }
            }

            if (i !== lines.length - 1) {
                fixedValue += line + separator;
            } else {
                fixedValue += line;
            }

            isPreviousLineEmpty = line.trim().length === 0;
        }

        return fixedValue;
    }

    private isListItem(nextLine: string): boolean {
        // eslint-disable-next-line no-null/no-null
        return nextLine.match(/^(\s{0,3})([-*+]|\d+\.)\s+/g) !== null;
    }

    private isHeading(line: string): boolean {
        // eslint-disable-next-line no-null/no-null
        return line.match(/^#{1,6}\s/) !== null;
    }

    private isThematicBreak(line: string): boolean {
        // A markdown thematic break is a line consisting solely of 3+ -, * or _.
        // eslint-disable-next-line no-null/no-null
        return line.trim().match(/^(-{3,}|\*{3,}|_{3,})$/) !== null;
    }

    private isBlockquote(line: string): boolean {
        // eslint-disable-next-line no-null/no-null
        return line.match(/^\s*>/) !== null;
    }

    private isCodeFence(line: string): boolean {
        // eslint-disable-next-line no-null/no-null
        return line.trim().match(/^(```|~~~)/) !== null;
    }

    private isTableRow(line: string): boolean {
        return line.includes('|');
    }

    private isTableDelimiter(line: string): boolean {
        // A GFM table delimiter row consists of cells of dashes with optional
        // alignment colons, separated by pipes, e.g. |---|:--:| or --- | ---
        // eslint-disable-next-line no-null/no-null
        return line.trim().match(/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?$/) !== null;
    }
}
